import React, { useState } from "react";

const MultiBusinessTotalsTable = ({ selectedBusinesses = [], compiledData = [] }) => {
	const normalize = (s) =>
		String(s || "")
			.trim()
			.toLowerCase();
	const selectedSet = new Set(selectedBusinesses.map(normalize));

	const [expandedBusinesses, setExpandedBusinesses] = useState({});
	const [expandedCategories, setExpandedCategories] = useState({});
	const [allExpanded, setAllExpanded] = useState(false);

	// Filter and de-duplicate by normalized business name to avoid duplicate rows
	const matched = (() => {
		const seen = new Set();
		const result = [];
		for (const b of compiledData) {
			const key = normalize(b.business);
			if (selectedSet.has(key) && !seen.has(key)) {
				seen.add(key);
				result.push(b);
			}
		}
		return result;
	})();
	if (matched.length === 0) return null;

	// Determine months length from first available category
	const monthsLength = matched.find((b) => b.categories && b.categories[0] && Array.isArray(b.categories[0].monthlyTotals))?.categories[0].monthlyTotals.length || 12;

	// Per-business monthly totals and overall combined
	const perBusinessMonthlyTotals = matched.map((b) => {
		const totals = Array(monthsLength).fill(0);
		b.categories.forEach((cat) => {
			cat.monthlyTotals.forEach((amt, idx) => {
				totals[idx] += amt;
			});
		});
		const total = totals.reduce((sum, val) => sum + val, 0);
		const denominator = b?.monthsInRange || monthsLength || 12;
		const average = total / denominator;
		return { name: b.business, months: totals, total, average, denominator };
	});

	// Aggregate monthly totals across all selected businesses and all categories
	const aggregatedMonthlyTotals = Array(monthsLength).fill(0);
	matched.forEach((b) => {
		b.categories.forEach((cat) => {
			cat.monthlyTotals.forEach((amt, idx) => {
				aggregatedMonthlyTotals[idx] += amt;
			});
		});
	});

	const grandTotal = aggregatedMonthlyTotals.reduce((sum, val) => sum + val, 0);
	const monthsDenominator =
		// Use monthsInRange from any matched business; they should be identical for a given query
		matched[0]?.monthsInRange || monthsLength || 12;
	const grandAverage = grandTotal / monthsDenominator;

	const monthLabels = Array.from({ length: monthsLength }, (_, idx) => {
		const date = new Date(2024, idx);
		return date.toLocaleString("en-US", { month: "short" });
	});

	const toggleBusiness = (businessName) => {
		setExpandedBusinesses((prev) => ({ ...prev, [businessName]: !prev[businessName] }));
	};

	const toggleCategory = (businessName, categoryName) => {
		const key = `${businessName}::${categoryName}`;
		setExpandedCategories((prev) => ({ ...prev, [key]: !prev[key] }));
	};

	const handleExpandCollapseAll = () => {
		const newStateBusinesses = {};
		const newStateCategories = {};
		matched.forEach((b) => {
			newStateBusinesses[b.business] = !allExpanded;
			b.categories.forEach((cat) => {
				if (cat.subcategories && cat.subcategories.length > 0) {
					const key = `${b.business}::${cat.category}`;
					newStateCategories[key] = !allExpanded;
				}
			});
		});
		setExpandedBusinesses(newStateBusinesses);
		setExpandedCategories(newStateCategories);
		setAllExpanded(!allExpanded);
	};

	return (
		<div className="table two-axis">
			<div className="row hd">
				<div className="col spacing">
					<a className="l-btn" onClick={handleExpandCollapseAll}>
						{allExpanded ? "-" : "+"}
					</a>
				</div>
				{monthLabels.map((label, idx) => (
					<div className="col" key={idx}>
						{label}
					</div>
				))}
				<div className="col total">Total</div>
				<div className="col total">Avg</div>
			</div>

			{matched.map((business, bIdx) => {
				const perBiz = perBusinessMonthlyTotals[bIdx];
				const isBizExpanded = expandedBusinesses[business.business] || false;
				const hasAnySubcats = business.categories.some((cat) => cat.subcategories && cat.subcategories.length > 0);
				return (
					<React.Fragment key={business.business}>
						<div className="row cat">
							<div className="col hd" onClick={() => hasAnySubcats && toggleBusiness(business.business)}>
								{business.business}
								{hasAnySubcats && <span className="expand-toggle">{isBizExpanded ? "−" : "+"}</span>}
							</div>
							{perBiz.months.map((val, idx) => (
								<div className={val !== 0 ? "col" : "col zero"} key={idx}>
									<span>{Math.round(val).toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0 })}</span>
								</div>
							))}
							<div className="col total">{Math.round(perBiz.total).toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0 })}</div>
							<div className="col total">{Math.round(perBiz.average).toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0 })}</div>
						</div>

						{business.categories.map((cat) => {
							const hasSubcategories = cat.subcategories && cat.subcategories.length > 0;
							const key = `${business.business}::${cat.category}`;
							const isCatExpanded = expandedCategories[key] || false;
							return (
								<React.Fragment key={key}>
									<div className={`row sub ${isBizExpanded ? "expanded" : "collapsed"}`}>
										<div className="col hd" onClick={() => hasSubcategories && toggleCategory(business.business, cat.category)}>
											{cat.category}
											{hasSubcategories && <span className="expand-toggle">{isCatExpanded ? "−" : "+"}</span>}
										</div>
										{cat.monthlyTotals.map((val, idx) => (
											<div className={val !== 0 ? "col" : "col zero"} key={idx}>
												<span>{Math.round(val).toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0 })}</span>
											</div>
										))}
										<div className="col total">{Math.round(cat.total).toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0 })}</div>
										<div className="col total">{Math.round(cat.monthlyAverage).toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0 })}</div>
									</div>

									{hasSubcategories &&
										cat.subcategories.map((subcat) => (
											<div key={`${key}::${subcat.category}`} className={`row sub ${isCatExpanded ? "expanded" : "collapsed"}`}>
												<div className="col hd">{subcat.category}</div>
												{subcat.monthlyTotals.map((val, idx) => (
													<div className={val !== 0 ? "col" : "col zero"} key={idx}>
														<span>
															{Math.round(val).toLocaleString("en-US", {
																style: "currency",
																currency: "USD",
																minimumFractionDigits: 0,
															})}
														</span>
													</div>
												))}
												<div className="col total">
													{Math.round(subcat.total).toLocaleString("en-US", {
														style: "currency",
														currency: "USD",
														minimumFractionDigits: 0,
													})}
												</div>
												<div className="col total">
													{Math.round(subcat.monthlyAverage).toLocaleString("en-US", {
														style: "currency",
														currency: "USD",
														minimumFractionDigits: 0,
													})}
												</div>
											</div>
										))}
								</React.Fragment>
							);
						})}
					</React.Fragment>
				);
			})}

			<div className="row footer-row">
				<div className="col hd">Combined Total</div>
				{aggregatedMonthlyTotals.map((val, idx) => (
					<div className={val !== 0 ? "col" : "col zero"} key={idx}>
						<span>{Math.round(val).toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0 })}</span>
					</div>
				))}
				<div className="col total">{Math.round(grandTotal).toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0 })}</div>
				<div className="col total">{Math.round(grandAverage).toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0 })}</div>
			</div>
		</div>
	);
};

export default MultiBusinessTotalsTable;
